import { supabase } from '@/integrations/supabase/client';

export interface DocumentAnalysis {
  documentType: string;
  personalData: {
    names: string[];
    cpf: string[];
    rg: string[];
    addresses: string[];
    phones: string[];
    emails: string[];
  };
  suggestedActions: ActionSuggestion[];
  confidence: 'high' | 'medium' | 'low';
}

export interface ActionSuggestion {
  id: string;
  title: string;
  description: string;
  icon: string;
  category: 'procuracao' | 'contrato' | 'peticao' | 'defesa' | 'calculo' | 'analise' | 'personalizado';
  question: string; // Nova propriedade para a pergunta formatada
}

/**
 * Analyzes extracted text and structured data to provide intelligent suggestions
 */
export async function analyzeDocument(
  extractedText: string,
  structuredData?: any
): Promise<DocumentAnalysis> {
  console.log('🔍 Analyzing document for intelligent suggestions...');
  
  const upperText = extractedText.toUpperCase();
  let documentType = 'DOCUMENTO_GENERICO';
  let confidence: 'high' | 'medium' | 'low' = 'low';
  
  // Extract personal data
  const personalData = {
    names: extractNames(extractedText),
    cpf: structuredData?.cpf || extractCPF(extractedText),
    rg: structuredData?.rg || extractRG(extractedText),
    addresses: extractAddresses(extractedText),
    phones: structuredData?.phones || extractPhones(extractedText),
    emails: structuredData?.emails || extractEmails(extractedText)
  };
  
  // Enhanced document type detection
  if (upperText.includes('CARTEIRA DE IDENTIDADE') || upperText.includes('CÉDULA DE IDENTIDADE') || upperText.includes('REGISTRO GERAL')) {
    documentType = 'DOCUMENTO_IDENTIDADE';
    confidence = 'high';
  } else if (upperText.includes('CPF') && upperText.includes('RECEITA FEDERAL')) {
    documentType = 'COMPROVANTE_CPF';
    confidence = 'high';
  } else if (upperText.includes('CERTIDÃO DE NASCIMENTO')) {
    documentType = 'CERTIDAO_NASCIMENTO';
    confidence = 'high';
  } else if (upperText.includes('CERTIDÃO DE CASAMENTO')) {
    documentType = 'CERTIDAO_CASAMENTO';
    confidence = 'high';
  } else if (upperText.includes('COMPROVANTE DE RENDA') || upperText.includes('DECLARAÇÃO DE RENDIMENTOS')) {
    documentType = 'COMPROVANTE_RENDA';
    confidence = 'high';
  } else if (upperText.includes('COMPROVANTE DE RESIDÊNCIA') || upperText.includes('CONTA DE LUZ') || upperText.includes('CONTA DE ÁGUA')) {
    documentType = 'COMPROVANTE_RESIDENCIA';
    confidence = 'high';
  } else if (upperText.includes('CONTRATO DE TRABALHO') || upperText.includes('CTPS')) {
    documentType = 'DOCUMENTO_TRABALHISTA';
    confidence = 'high';
  } else if (upperText.includes('CONTRATO')) {
    documentType = 'CONTRATO';
    confidence = 'high';
  } else if (upperText.includes('PETIÇÃO') || upperText.includes('EXCELENTÍSSIMO')) {
    documentType = 'PETIÇÃO_JURIDICA';
    confidence = 'high';
  } else if (upperText.includes('PROCURAÇÃO')) {
    documentType = 'PROCURAÇÃO';
    confidence = 'high';
  } else if (upperText.includes('BOLETIM DE OCORRÊNCIA') || upperText.includes('B.O.')) {
    documentType = 'BOLETIM_OCORRENCIA';
    confidence = 'high';
  } else if (personalData.names.length > 0 || personalData.cpf.length > 0) {
    documentType = 'DOCUMENTO_PESSOAL';
    confidence = 'medium';
  }
  
  // Generate enhanced action suggestions
  const suggestedActions = generateEnhancedActionSuggestions(documentType, personalData, extractedText);
  
  console.log(`📋 Document analysis complete: ${documentType} (${confidence} confidence)`);
  console.log(`📊 Found: ${personalData.names.length} names, ${personalData.cpf.length} CPFs`);
  
  return {
    documentType,
    personalData,
    suggestedActions,
    confidence
  };
}

function extractNames(text: string): string[] {
  const lines = text.split('\n').map(line => line.trim());
  const names: string[] = [];
  
  for (const line of lines) {
    if (line.match(/^[A-ZÁÊÇÕ][a-záêçõ]+(?:\s+[A-ZÁÊÇÕ][a-záêçõ]+)+$/)) {
      if (!line.includes('@') && !line.match(/\d/) && line.length > 5 && line.length < 50) {
        names.push(line);
      }
    }
    
    const nameMatch = line.match(/(?:Nome|NOME):\s*([A-ZÁÊÇÕ][a-záêçõ\s]+)/);
    if (nameMatch) {
      names.push(nameMatch[1].trim());
    }
  }
  
  return [...new Set(names)].slice(0, 5);
}

function extractCPF(text: string): string[] {
  const cpfRegex = /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g;
  const matches = text.match(cpfRegex);
  return matches ? [...new Set(matches)] : [];
}

function extractRG(text: string): string[] {
  const rgRegex = /\b\d{1,2}\.?\d{3}\.?\d{3}-?[0-9xX]\b/g;
  const matches = text.match(rgRegex);
  return matches ? [...new Set(matches)] : [];
}

function extractAddresses(text: string): string[] {
  const addresses: string[] = [];
  const lines = text.split('\n').map(line => line.trim());
  
  for (const line of lines) {
    if (line.match(/(?:Rua|Av|Avenida|Alameda|Travessa|Praça)/i) && 
        line.match(/\d+/) && 
        line.length > 10) {
      addresses.push(line);
    }
    
    const addressMatch = line.match(/(?:Endereço|ENDEREÇO):\s*(.+)/);
    if (addressMatch) {
      addresses.push(addressMatch[1].trim());
    }
  }
  
  return [...new Set(addresses)].slice(0, 3);
}

function extractPhones(text: string): string[] {
  const phoneRegex = /\b(?:\+55\s?)?(?:\(\d{2}\)\s?)?\d{4,5}-?\d{4}\b/g;
  const matches = text.match(phoneRegex);
  return matches ? [...new Set(matches)] : [];
}

function extractEmails(text: string): string[] {
  const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
  const matches = text.match(emailRegex);
  return matches ? [...new Set(matches)] : [];
}

function generateEnhancedActionSuggestions(
  documentType: string,
  personalData: any,
  extractedText: string
): ActionSuggestion[] {
  const suggestions: ActionSuggestion[] = [];
  
  switch (documentType) {
    case 'DOCUMENTO_IDENTIDADE':
    case 'COMPROVANTE_CPF':
    case 'CERTIDAO_NASCIMENTO':
    case 'CERTIDAO_CASAMENTO':
      suggestions.push(
        {
          id: 'procuracao_simples',
          title: '📋 Criar Procuração Simples',
          description: 'Gerar procuração com os dados identificados do documento',
          icon: '📋',
          category: 'procuracao',
          question: 'Com base no documento enviado, criar uma procuração simples usando os dados identificados.'
        },
        {
          id: 'procuracao_especifica',
          title: '📝 Criar Procuração Específica',
          description: 'Procuração com poderes específicos personalizados',
          icon: '📝',
          category: 'procuracao',
          question: 'Criar uma procuração específica com poderes personalizados usando os dados do documento.'
        },
        {
          id: 'contrato_honorarios',
          title: '💰 Contrato de Honorários',
          description: 'Criar contrato de honorários advocatícios',
          icon: '💰',
          category: 'contrato',
          question: 'Elaborar um contrato de honorários advocatícios com os dados identificados.'
        }
      );
      break;
      
    case 'COMPROVANTE_RENDA':
    case 'COMPROVANTE_RESIDENCIA':
      suggestions.push(
        {
          id: 'contrato_locacao',
          title: '🏠 Contrato de Locação',
          description: 'Minuta de contrato usando os dados pessoais',
          icon: '🏠',
          category: 'contrato',
          question: 'Criar um contrato de locação residencial com base nos dados do comprovante.'
        },
        {
          id: 'fiador_locacao',
          title: '🤝 Termo de Fiança',
          description: 'Documento de fiança para locação',
          icon: '🤝',
          category: 'contrato',
          question: 'Elaborar termo de fiança para locação usando os dados fornecidos.'
        }
      );
      break;
      
    case 'CONTRATO':
      suggestions.push(
        {
          id: 'analise_contrato',
          title: '🔍 Análise Jurídica do Contrato',
          description: 'Revisar cláusulas e identificar pontos de atenção',
          icon: '🔍',
          category: 'analise',
          question: 'Fazer uma análise jurídica completa do contrato, identificando cláusulas problemáticas e sugerindo melhorias.'
        },
        {
          id: 'aditivo_contrato',
          title: '📑 Criar Aditivo Contratual',
          description: 'Gerar aditivo baseado no contrato existente',
          icon: '📑',
          category: 'contrato',
          question: 'Criar um aditivo contratual baseado nas informações do contrato original.'
        }
      );
      break;
      
    case 'DOCUMENTO_TRABALHISTA':
      suggestions.push(
        {
          id: 'defesa_trabalhista',
          title: '⚖️ Defesa Trabalhista',
          description: 'Elaborar defesa baseada nos documentos apresentados',
          icon: '⚖️',
          category: 'defesa',
          question: 'Elaborar uma defesa trabalhista robusta com base na documentação apresentada.'
        },
        {
          id: 'peticao_trabalhista',
          title: '📄 Petição Trabalhista',
          description: 'Criar petição inicial com os dados do processo',
          icon: '📄',
          category: 'peticao',
          question: 'Redigir uma petição inicial trabalhista usando as informações dos documentos.'
        },
        {
          id: 'calculo_trabalhista',
          title: '🧮 Cálculo Trabalhista',
          description: 'Auxiliar no cálculo de verbas rescisórias',
          icon: '🧮',
          category: 'calculo',
          question: 'Ajudar a calcular as verbas trabalhistas devidas com base na documentação.'
        }
      );
      break;
      
    case 'BOLETIM_OCORRENCIA':
      suggestions.push(
        {
          id: 'queixa_crime',
          title: '👮 Queixa-Crime',
          description: 'Elaborar queixa-crime baseada no B.O.',
          icon: '👮',
          category: 'peticao',
          question: 'Elaborar uma queixa-crime com base nas informações do boletim de ocorrência.'
        },
        {
          id: 'representacao_criminal',
          title: '⚖️ Representação Criminal',
          description: 'Criar representação para o Ministério Público',
          icon: '⚖️',
          category: 'peticao',
          question: 'Redigir representação criminal ao Ministério Público baseada no B.O.'
        }
      );
      break;
      
    default:
      suggestions.push(
        {
          id: 'analise_geral',
          title: '📊 Análise Jurídica Geral',
          description: 'Análise completa do documento e orientações',
          icon: '📊',
          category: 'analise',
          question: 'Fazer uma análise jurídica completa do documento e fornecer orientações práticas.'
        },
        {
          id: 'documento_personalizado',
          title: '✍️ Documento Personalizado',
          description: 'Criar documento jurídico específico com base no conteúdo',
          icon: '✍️',
          category: 'personalizado',
          question: 'Criar um documento jurídico personalizado com base no conteúdo analisado.'
        },
        {
          id: 'contestacao',
          title: '📋 Contestação Judicial',
          description: 'Elaborar contestação para processo judicial',
          icon: '📋',
          category: 'defesa',
          question: 'Elaborar uma contestação judicial robusta baseada na documentação.'
        }
      );
  }
  
  // Adicionar sempre algumas sugestões universais
  suggestions.push(
    {
      id: 'procuracao_geral',
      title: '📋 Procuração por Instrumento Particular',
      description: 'Procuração geral com dados do documento',
      icon: '📋',
      category: 'procuracao',
      question: 'Criar uma procuração por instrumento particular usando os dados identificados.'
    },
    {
      id: 'contrato_servicos',
      title: '💼 Contrato de Prestação de Serviços',
      description: 'Contrato profissional com os dados identificados',
      icon: '💼',
      category: 'contrato',
      question: 'Elaborar contrato de prestação de serviços advocatícios com os dados do documento.'
    }
  );
  
  return suggestions.slice(0, 6); // Limitar a 6 sugestões para não sobrecarregar
}
